import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { msalInstance, loginRequest } from '../services/msalConfig';
import { loginWithMicrosoft } from '../services/authApi';
import { useAuthStore } from '../stores/authStore';
import Spinner from '../components/ui/Spinner';

// Handles the MSAL redirect return: resolve the redirect promise, acquire an
// access token silently, exchange it with the backend for our app JWT + user.
export default function AuthMicrosoftCallback() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        await msalInstance.initialize();
        const result = await msalInstance.handleRedirectPromise();
        const account =
          result?.account || msalInstance.getAllAccounts()[0] || null;

        if (!account) {
          setError('No Microsoft account found. Please try signing in again.');
          return;
        }
        msalInstance.setActiveAccount(account);

        // Prefer the tokens from the redirect result; otherwise acquire them
        // silently for the same account. The backend verifies the id_token; the
        // access_token is sent alongside.
        let idToken = result?.idToken;
        let accessToken = result?.accessToken;
        if (!idToken) {
          const silent = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          idToken = silent.idToken;
          accessToken = silent.accessToken;
        }

        const { token, user } = await loginWithMicrosoft(idToken, accessToken);
        setAuth(token, user);
        navigate('/', { replace: true });
      } catch {
        setError('Microsoft sign-in failed. Please try again.');
      }
    })();
  }, [navigate, setAuth]);

  return (
    <div className="min-h-screen grid place-items-center bg-ws-bg">
      {error ? (
        <div className="text-center">
          <p className="text-sm text-ws-danger">{error}</p>
          <button className="mt-3 text-sm text-ws-accent underline" onClick={() => navigate('/login')}>
            Back to sign in
          </button>
        </div>
      ) : (
        <Spinner size={22} label="Completing Microsoft sign-in…" />
      )}
    </div>
  );
}
